import { useRetainedRealtimeState } from '../../services/firebase/use-retained-realtime-state'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  mergeInboxPages,
  parseAggregateInboxPage,
  parseInboxPage,
  type InboxPage,
} from './conversations-pagination'
import { interactionAggregateReadinessSchema } from './conversations-contracts'
import {
  errorKind,
  type ConversationRepository,
} from './conversations-repository'

type InboxState = {
  owner: string | null
  status: 'loading' | 'ready' | 'forbidden' | 'error'
  newest: InboxPage | null
  historical: InboxPage[]
  loadingOlder: boolean
  mode: 'legacy' | 'canonical' | null
}

export function useConversationInbox(
  repository: ConversationRepository,
  tenantId: string | null
) {
  const generation = useRef(0)
  const [state, setState, resume] = useRetainedRealtimeState<InboxState>(
    ['inbox', tenantId],
    {
      owner: tenantId,
      status: 'loading',
      newest: null,
      historical: [],
      loadingOlder: false,
      mode: null,
    },
    (value) => value.status === 'ready'
  )

  useEffect(() => {
    const currentGeneration = ++generation.current
    resume()
    if (!tenantId) return

    let unsubscribe: (() => void) | undefined
    const subscribe = (
      mode: 'legacy' | 'canonical',
      method: ConversationRepository['subscribeInbox'],
      parser: typeof parseInboxPage
    ) => {
      setState((current) =>
        current.mode !== null && current.mode !== mode
          ? {
              owner: tenantId,
              status: 'loading',
              newest: null,
              historical: [],
              loadingOlder: false,
              mode,
            }
          : { ...current, loadingOlder: false }
      )
      unsubscribe = method(
        tenantId,
        (value) => {
          if (generation.current !== currentGeneration) return
          setState((current) => ({
            ...current,
            mode,
            status: 'ready',
            newest: parser(value),
          }))
        },
        (error) => {
          if (generation.current !== currentGeneration) return
          setState((current) => ({
            ...current,
            mode,
            status: errorKind(error) === 'forbidden' ? 'forbidden' : 'error',
            newest: null,
            historical: [],
          }))
        }
      )
    }

    void (async () => {
      try {
        if (!repository.getAggregateReadiness) {
          subscribe('legacy', repository.subscribeInbox, parseInboxPage)
          return
        }
        const readiness = await repository.getAggregateReadiness(tenantId)
        const parsed = interactionAggregateReadinessSchema.safeParse(readiness)
        if (generation.current !== currentGeneration) return
        if (!parsed.success && readiness !== null) {
          throw new Error('aggregate readiness is malformed')
        }
        if (!parsed.success || parsed.data.status === 'NOT_READY') {
          subscribe('legacy', repository.subscribeInbox, parseInboxPage)
          return
        }
        if (!repository.subscribeAggregateInbox) {
          throw new Error('canonical aggregate repository is unavailable')
        }
        subscribe(
          'canonical',
          repository.subscribeAggregateInbox,
          parseAggregateInboxPage
        )
      } catch (error) {
        if (generation.current !== currentGeneration) return
        setState((current) => ({
          ...current,
          status: errorKind(error) === 'forbidden' ? 'forbidden' : 'error',
          newest: null,
          historical: [],
        }))
      }
    })()

    return () => {
      generation.current += 1
      unsubscribe?.()
    }
  }, [repository, tenantId, resume, setState])

  const effectiveState =
    state.owner === tenantId
      ? state
      : {
          owner: tenantId,
          status: 'loading' as const,
          newest: null,
          historical: [],
          loadingOlder: false,
          mode: null,
        }

  const conversations = useMemo(
    () => mergeInboxPages(effectiveState.newest, effectiveState.historical),
    [effectiveState.historical, effectiveState.newest]
  )
  const lastPage =
    effectiveState.historical.at(-1) ?? effectiveState.newest ?? null

  const loadOlder = useCallback(async () => {
    if (
      !tenantId ||
      !lastPage?.hasOlder ||
      !lastPage.cursor ||
      effectiveState.loadingOlder
    ) {
      return
    }
    const requestGeneration = generation.current
    setState((current) => ({ ...current, loadingOlder: true }))
    try {
      const value =
        effectiveState.mode === 'canonical'
          ? await repository.getOlderAggregateInbox?.(tenantId, lastPage.cursor)
          : await repository.getOlderInbox(tenantId, lastPage.cursor)
      if (effectiveState.mode === 'canonical' && value === undefined) {
        throw new Error('canonical aggregate pagination is unavailable')
      }
      if (generation.current !== requestGeneration) return
      const page =
        effectiveState.mode === 'canonical'
          ? parseAggregateInboxPage(value)
          : parseInboxPage(value)
      setState((current) => ({
        ...current,
        historical: [...current.historical, page],
        loadingOlder: false,
      }))
    } catch (error) {
      if (generation.current !== requestGeneration) return
      setState((current) => ({
        ...current,
        status: errorKind(error) === 'forbidden' ? 'forbidden' : 'error',
        loadingOlder: false,
      }))
    }
  }, [
    effectiveState.loadingOlder,
    effectiveState.mode,
    lastPage,
    repository,
    tenantId,
    setState,
  ])

  return {
    status: effectiveState.status,
    // Which list the clinic's inbox reads; a search must read the same one.
    mode: effectiveState.mode,
    newestPage: effectiveState.newest,
    conversations,
    hasOlder: lastPage?.hasOlder ?? false,
    loadingOlder: effectiveState.loadingOlder,
    loadOlder,
  }
}
